import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { AppFooter } from "scmp-app/components/app-footer";
import { ContentItemSpecialIndex } from "scmp-app/components/content/content-item-render/variants/special-index";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PageSeo } from "scmp-app/components/meta";
import { useKeywordMetaTags } from "scmp-app/components/meta/hooks";
import { SpecialIndexHeader } from "scmp-app/components/special-index/special-index-header";
import { trackingAtom } from "scmp-app/components/tracking";
import { useCanonicalUrl, useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { specialIndexPageQuery } from "scmp-app/queries/__generated__/specialIndexPageQuery.graphql";
import type { specialIndexPageQueueFragment$key } from "scmp-app/queries/__generated__/specialIndexPageQueueFragment.graphql";

import {
  getAdditionalKeyword,
  getContentItemVariant,
  getDescription,
  getImageAspectRatio,
  getLogo,
  getMetaTitle,
  getOpenGraphImageUrl,
  getPath,
  getShouldShowContentTypeIcon,
  getShouldShowDefaultImage,
} from "./helpers";
import {
  Background,
  ContentLinksContainer,
  InfiniteScrollPlaceholder,
  MainContainer,
} from "./styles";

export const query = graphql`
  query specialIndexPageQuery($queueName: String!) {
    ...specialIndexPageQueueFragment @arguments(queueName: $queueName)
  }
`;

const specialIndexPageSize = 20;

const SpecialIndexPage: RelayComponent<unknown, specialIndexPageQuery> = ({ preloadedQuery }) => {
  const preloadedData = usePreloadedQuery(query, preloadedQuery);

  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<specialIndexPageQuery, specialIndexPageQueueFragment$key>(
    graphql`
      fragment specialIndexPageQueueFragment on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 20 }
        queueName: { type: "String!" }
      )
      @refetchable(queryName: "specialIndexPageQueuePaginationQuery") {
        queue(filter: { name: $queueName }) {
          items(after: $after, first: $count)
            @connection(key: "specialIndexPageQueueFragment_items") {
            edges {
              node {
                ... on Content {
                  entityId
                  ...specialIndexContentItemContent
                }
              }
            }
          }
        }
      }
    `,
    preloadedData,
  );

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(specialIndexPageSize),
  });

  const queueName = preloadedQuery.variables.queueName;

  const keywordTags = useKeywordMetaTags(getAdditionalKeyword(queueName), true);

  const { canonicalUrl } = useCanonicalUrl(getPath(queueName));

  const description = getDescription(queueName);

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  return (
    <>
      <PageSeo
        additionalMetaTags={[...keywordTags]}
        canonical={canonicalUrl}
        description={description}
        openGraph={{
          images: [
            {
              url: getOpenGraphImageUrl(queueName),
            },
          ],
          url: canonicalUrl,
        }}
        title={getMetaTitle(queueName)}
      />
      <Background>
        <MainContainer>
          <SpecialIndexHeader description={description} logo={getLogo(queueName)} />
        </MainContainer>
        <ContentLinksContainer>
          {data.queue?.items?.edges.map(
            edge =>
              "entityId" in edge.node && (
                <ContentItemSpecialIndex
                  imageAspectRatio={getImageAspectRatio(queueName)}
                  isCommentsVisible={false}
                  key={edge.node.entityId}
                  reference={edge.node}
                  shouldShowContentTypeIcon={getShouldShowContentTypeIcon(queueName)}
                  shouldShowDefaultImage={getShouldShowDefaultImage(queueName)}
                  variant={getContentItemVariant(queueName)}
                />
              ),
          )}
        </ContentLinksContainer>
        {hasNextPage && (
          <InfiniteScrollPlaceholder ref={element => infiniteScrollTriggerReference(element)} />
        )}
      </Background>
      {!hasNextPage && <AppFooter variant="dark" />}
    </>
  );
};

SpecialIndexPage.displayName = "SpecialIndexPage";

const pageProps: PageProps = {
  appBarConfiguration: {
    desktopBreakpoint: "desktop",
    hasDesktopAd: false,
    hasMobileAd: false,
    variant: "scmp/slim",
  },
  contentConfiguration: {
    responsivePadding: {
      tabletUp: 4,
    },
  },
};

export default withRelay(SpecialIndexPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    const queueName = context.query.queueName as string;

    return {
      queueName,
    };
  },
});
